import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { baseurl } from '../Baseurl';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaShare, FaCopy, FaTimes, FaPlusCircle, FaEdit, FaSave } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';

export default function ShareProject() {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [accessType, setAccessType] = useState('permanent');
  const [expiresAt, setExpiresAt] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  const [allShares, setAllShares] = useState([]);
  const [editingToken, setEditingToken] = useState(null);
  const [editAccessType, setEditAccessType] = useState('');
  const [editExpiresAt, setEditExpiresAt] = useState(null);

  const { id, title } = useParams();

  const fetchShares = async () => {
    try {
      const res = await axios.get(`${baseurl}getSharedLinksByProjectId/${id}`);
      const now = new Date();

      const activeLinks = res.data.filter((link) => {
        // if (!link.is_active) return false;
        if (link.access_type === 'time_bound' && link.expires_at) {
          return new Date(link.expires_at) > now;
        }
        return true;
      });

      setAllShares(activeLinks);
    } catch (err) {
      console.error('Failed to load shared links');
    }
  };

  useEffect(() => {
    if (showModal) fetchShares();
  }, [showModal]);

  const handleCreateLink = async () => {
    if (!email) {
      Swal.fire('Error', 'Please enter an email address', 'error');
      return;
    }

    const generatedToken = uuidv4();
    const payload = {
      project_id: id,
      allowed_user_email: email,
      access_type: accessType,
      expires_at: accessType === 'time_bound' ? expiresAt : null,
      token: generatedToken,
      title: title,

    };

    try {
      await axios.post(`${baseurl}insertSharedLink`, payload);
      Swal.fire('Success', 'Link created', 'success');
      setEmail('');
      setAccessType('permanent');
      setExpiresAt(null);
      fetchShares();
      setActiveTab('manage');
    } catch (error) {
      Swal.fire('Error', 'Failed to create link', 'error');
    }
  };

const handleRevoke = async (token, email) => {
  Swal.fire({
    title: "Are you sure?",
    text: `Do you want to change access for ${email}?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, continue",
    cancelButtonText: "Cancel",
    reverseButtons: true,
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        console.log("📤 Sending revoke payload:", { token, email });

        const res = await axios.post(`${baseurl}revokeaccess`, { token, email });

        console.log("✅ Server response:", res.data);

        if (res.data.success) {
          Swal.fire({
            icon: res.data.is_active ? "success" : "info",
            title: res.data.message,
            timer: 2000,
            showConfirmButton: false,
          });
          fetchShares();
        } else {
          Swal.fire("Error", res.data.message || "Failed to update access", "error");
        }
      } catch (error) {
        console.error("❌ Error revoking/granting access:", error);
        Swal.fire("Error", "Server error while updating access", "error");
      }
    }
  });
};


  const handleEdit = (link) => {
    setEditingToken(link.token);
    setEditAccessType(link.access_type);
    setEditExpiresAt(link.expires_at ? new Date(link.expires_at) : null);
  };

  const handleSaveEdit = async (token) => {
    try {
      await axios.post(`${baseurl}updateSharedLink`, {
        token,
        access_type: editAccessType,
        expires_at: editAccessType === 'time_bound' ? editExpiresAt : null
      });
      Swal.fire('Updated!', 'Access updated successfully.', 'success');
      setEditingToken(null);
      fetchShares();
    } catch (err) {
      Swal.fire('Error', 'Failed to update access.', 'error');
    }
  };

  const copyToClipboard = (link) => {
    navigator.clipboard.writeText(link);
    Swal.fire('Copied!', '', 'success');
  };

  const renderShareLink = (token) => `${window.location.origin}/shared-access/${token}`;

  return (
    <>
      <button className="btn btn-link text-white" onClick={() => setShowModal(true)}>
        <FaShare size={20} />
      </button>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content text-dark">
              <div className="modal-header">
                <h5 className="modal-title">Share Project</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>

              <div className="modal-body">
                {/* Tabs */}
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'create' ? 'active' : ''}`}
                      onClick={() => setActiveTab('create')}
                    >
                      <FaPlusCircle className="me-1" /> Create New
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'manage' ? 'active' : ''}`}
                      onClick={() => setActiveTab('manage')}
                    >
                      Manage Links
                    </button>
                  </li>
                </ul>

                {activeTab === 'create' && (
                  <>
                    <div className="mb-3">
                      <label>Email to share with:</label>
                      <input
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <label>Access Type:</label>
                      <select
                        className="form-select"
                        value={accessType}
                        onChange={(e) => setAccessType(e.target.value)}
                      >
                        <option value="permanent">Permanent</option>
                        <option value="time_bound">Time Bound</option>
                        <option value="one_time">One Time</option>
                      </select>
                    </div>

                    {accessType === 'time_bound' && (
                      <div className="mb-3">
                        <label>Expires At:</label>
                        <DatePicker
                          selected={expiresAt}
                          onChange={(date) => setExpiresAt(date)}
                          showTimeSelect
                          dateFormat="Pp"
                          className="form-control"
                        />
                      </div>
                    )}

                    <button className="btn btn-primary w-100" onClick={handleCreateLink}>
                      Generate Link
                    </button>
                  </>
                )}

                {activeTab === 'manage' && (
                  <>
                    {allShares.length === 0 ? (
                      <div className="alert alert-warning">No active share links.</div>
                    ) : (
                      <table className="custom-table">
                        <thead className=" ">
                          <tr>
                            <th>Email</th>
                            <th>Access</th>
                            <th>Expires</th>
                            <th>Link</th>
                            <th>Revoked Access</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allShares.map((row) => (
                            <tr key={row.token}>
                              <td>{row.allowed_user_email}</td>
                              <td>
                                {editingToken === row.token ? (
                                  <select
                                    className="form-select"
                                    value={editAccessType}
                                    onChange={(e) => setEditAccessType(e.target.value)}
                                  >
                                    <option value="permanent">Permanent</option>
                                    <option value="time_bound">Time Bound</option>
                                    <option value="one_time">One Time</option>
                                  </select>
                                ) : (
                                  row.access_type
                                )}
                              </td>
                              <td>
                                {editingToken === row.token && editAccessType === 'time_bound' ? (
                                  <DatePicker
                                    selected={editExpiresAt}
                                    onChange={(date) => setEditExpiresAt(date)}
                                    showTimeSelect
                                    dateFormat="Pp"
                                    className="form-control"
                                  />
                                ) : row.expires_at ? (
                                  new Date(row.expires_at).toLocaleString()
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="text-break">{renderShareLink(row.token)}</td> 
                             <td>
  {row.is_active ? (
    <span className="badge bg-success">Active</span>
  ) : (
    <span className="badge bg-danger">Revoked</span>
  )}
</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-dark me-2"
                                  onClick={() => copyToClipboard(renderShareLink(row.token))}
                                >
                                  <FaCopy />
                                </button>
                                {editingToken === row.token ? (
                                  <button
                                    className="btn btn-sm btn-outline-success me-2"
                                    onClick={() => handleSaveEdit(row.token)}
                                  >
                                    <FaSave />
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-outline-primary me-2"
                                    onClick={() => handleEdit(row)}
                                  >
                                    <FaEdit />
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleRevoke(row.token, row.allowed_user_email)}
                                >
                                  <FaTimes />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
